import { jwtDecode, JwtPayload } from 'jwt-decode';
import React, { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { camelCase } from 'change-case';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';
import { checkPermissions } from '#shared/permissions/index';
import PermissionsContext, {
  PermissionsContract,
} from '../contexts/PermissionsContext';
import { formatPermissions } from '#shared/permissions/utils';

export type PermissionsToken = {
  isAdmin: boolean;
  permissions: { [key: string]: string[] };
};

export type DecodedToken = JwtPayload & PermissionsToken;

const CommonPermissions: React.FC<{
  resourcePermissions: Record<string, Record<string, boolean>>;
  refetchPermissions?: () => void;
}> = ({ children, resourcePermissions, refetchPermissions }) => {
  const {
    keycloak,
    groups,
    isAuthenticated,
    roles,
    permissionToken,
    permissionList,
  }: any = useAuth(); // JME2
  const flags = useFlags();

  const hasResourcePermission: PermissionsContract['hasResourcePermission'] = useCallback(
    (resourceArg, actionArg, requireAny) => {
      const resources =
        typeof resourceArg === 'string' ? [resourceArg] : resourceArg;
      const actions = typeof actionArg === 'string' ? [actionArg] : actionArg;

      const resourceActions = resources.reduce(
        (carry, r) => [
          ...carry,
          ...actions.map((a) => ({ resource: r, action: a })),
        ],
        [] as { resource: string; action: string }[],
      );

      return resourceActions.reduce((allow, { resource, action }) => {
        const allowForResourceAction =
          typeof resourcePermissions[resource] !== 'undefined' &&
          resourcePermissions[resource][action] === true;
        if (requireAny) {
          return allow || allowForResourceAction;
        }
        return allow && allowForResourceAction;
      }, !requireAny);
    },
    [resourcePermissions],
  );

  const hasResourcePermissionNew: PermissionsContract['hasResourcePermissionNew'] = useCallback(
    ({
      action,
      year,
      institutionId,
      sdgId,
      availableImpactYears,
      isDuringEmbargo,
    }: {
      action: string;
      year?: number;
      institutionId: string;
      sdgId?: number;
      availableImpactYears?: number[];
      isDuringEmbargo?: boolean;
    }) =>
      checkPermissions({
        permissionToken,
        permissionList,
        action,
        year,
        institutionId,
        sdgId,
        availableImpactYears,
        isDuringEmbargo,
      }),
    [permissionToken, permissionList],
  );

  const getAvailableSdgYears = (
    availableImpactYears,
    institutionId,
    isDuringEmbargo = false,
  ) => {
    const decodedToken: DecodedToken = jwtDecode(permissionToken);

    const { permissions, isAdmin } = decodedToken;

    if (isAdmin) return availableImpactYears;

    if (permissions && permissions['*'] && permissions['*'].length)
      return availableImpactYears;

    return availableImpactYears.filter((year) =>
      hasResourcePermissionNew({
        action: 'sdgImpact:viewYear',
        year,
        institutionId,
        isDuringEmbargo,
        availableImpactYears,
      }),
    );
  };

  const getYearsForSDGRole = (role, availableImpactYears) => {
    const decodedToken: DecodedToken = jwtDecode(permissionToken);

    const { permissions, isAdmin } = decodedToken;

    if (isAdmin) {
      return availableImpactYears;
    }

    if (permissions && permissions['*'] && permissions['*'].length) {
      return availableImpactYears;
    }

    if (availableImpactYears?.length) {
      switch (role) {
        case 'SDG_IMPACT_SUBSCRIPTION_PREMIUM':
          return availableImpactYears;
        case 'SDG_IMPACT_SUBSCRIPTION_ADVANCED':
          return availableImpactYears.slice(0, 2);
        case 'SDG_IMPACT_SUBSCRIPTION_BASIC':
          return availableImpactYears.slice(0, 1);
      }
    }

    return [];
  };

  const getFirstRoleOfUser = useCallback(
    <T extends string>(
      rolesToCheck: readonly T[],
      institutionId: string,
    ): T | null => {
      const decodedToken: DecodedToken = jwtDecode(permissionToken);

      const { permissions } = decodedToken;
      const formattedPermissions = formatPermissions(
        permissions,
        permissionList,
      );

      const institutionPermissions =
        permissions['*'] && permissions['*'].length
          ? formattedPermissions['*']
          : formattedPermissions[institutionId];

      return (
        rolesToCheck.find((role) =>
          institutionPermissions.some((permission) =>
            permission.includes(role),
          ),
        ) ?? null
      );
    },
    [permissionToken, permissionList],
  );

  const hasRole: PermissionsContract['hasRole'] = useCallback(
    (requiredRoles, requireAny) => {
      if (typeof requiredRoles === 'string') {
        if (flags[camelCase('auth0Enabled')]) {
          return !isAuthenticated ? false : roles.includes(requiredRoles);
        }

        return typeof keycloak === 'undefined'
          ? false
          : keycloak.hasRealmRole(requiredRoles);
      }
      return requiredRoles.reduce((allow, role) => {
        if (requireAny) {
          return allow || hasRole(role);
        }
        return allow && hasRole(role);
      }, !requireAny);
    },
    [keycloak, isAuthenticated, roles, flags],
  );

  const hasClientRole: PermissionsContract['hasClientRole'] = useCallback(
    (client, requiredRoles, requireAny) => {
      if (typeof requiredRoles === 'string') {
        if (flags[camelCase('auth0Enabled')]) {
          return !isAuthenticated ? false : roles.includes(requiredRoles);
        }

        return typeof keycloak === 'undefined'
          ? false
          : keycloak.hasResourceRole(requiredRoles, client);
      }
      return requiredRoles.reduce((allow, role) => {
        if (requireAny) {
          return allow || hasRole(role);
        }
        return allow && hasRole(role);
      }, !requireAny);
    },
    [keycloak, hasRole, roles, isAuthenticated, flags],
  );

  const hasGroup: PermissionsContract['hasGroup'] = useCallback(
    (requiredGroups, requireAny) => {
      if (typeof requiredGroups === 'string') {
        if (flags[camelCase('auth0Enabled')]) {
          return !isAuthenticated ? false : groups.includes(requiredGroups);
        }

        return groups.includes(requiredGroups);
      }
      return requiredGroups.reduce((allow, group) => {
        if (requireAny) {
          return allow || hasGroup(group);
        }
        return allow && hasGroup(group);
      }, !requireAny);
    },
    [groups, isAuthenticated, flags],
  );

  const getUserAvailableYearsForProduct = useCallback(
    (institutionId: string, partialProductName: string) => {
      const decodedToken: DecodedToken = jwtDecode(permissionToken);

      const { permissions } = decodedToken;
      const formattedPermissions = formatPermissions(
        permissions,
        permissionList,
      );

      const availablePermissions = (
        formattedPermissions[institutionId] ||
        formattedPermissions['*'] ||
        []
      ).filter((permission: string) => permission.includes(partialProductName));

      const yearRegex = /_(\d{4})_/;
      const uniqueYears = new Set();
      availablePermissions.forEach((subscription: string) => {
        const match = subscription.match(yearRegex);
        if (match) {
          uniqueYears.add(Number(match[1]));
        }
      });

      if (uniqueYears.size > 0) {
        const uniqueYearsList = Array.from(uniqueYears).sort();
        return uniqueYearsList;
      }

      return null;
    },
    [permissionToken, permissionList],
  );

  return (
    <PermissionsContext.Provider
      value={{
        resources: resourcePermissions,
        refetchPermissions,
        hasResourcePermission,
        hasResourcePermissionNew,
        getFirstRoleOfUser,
        getYearsForSDGRole,
        getAvailableSdgYears,
        hasRole,
        hasClientRole,
        hasGroup,
        getUserAvailableYearsForProduct,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export default CommonPermissions;
